import { DateFnsHelpersService } from '@lockton-apps/shared/util/ngx-date-fns-tz';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormlyConfig, ɵobserve as observe } from '@ngx-formly/core';
import { FormlyFieldDatepicker } from '@ngx-formly/material/datepicker';
import { add, parse, sub, toDate } from 'date-fns';
import { isNil, replace, split } from 'lodash';
import { DynamicFormsLocaleConfig } from '../providers/config';
import { LOCALE_CONFIG } from '@lockton-apps/shared/util/localization';
import { MatDatepicker } from '@angular/material/datepicker';
import { fromEvent, merge, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'lockton-apps-datepicker-type',
    templateUrl: './datepicker-type.component.html',
    styleUrls: ['./datepicker-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DatepickerTypeComponent
  extends FormlyFieldDatepicker
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatDatepicker, { static: true }) datepicker!: MatDatepicker<Date>;
  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;

  min!: Date | null | undefined;
  max!: Date | null | undefined;

  private readonly destroyed$ = new Subject<void>();

  readonly defaultPlaceholder: string;
  readonly defaultMask: string;

  constructor(
    config: FormlyConfig,
    cdRef: ChangeDetectorRef,
    @Inject(LOCALE_CONFIG) localeConfig: DynamicFormsLocaleConfig,
    private dateFnsHelpers: DateFnsHelpersService
  ) {
    super(config, cdRef);

    this.defaultPlaceholder = localeConfig.datePlaceholder;
    this.defaultMask = localeConfig.dateMask;
  }

  ngOnInit(): void {
    this.setMinDate(this.props.datepickerOptions?.min);
    this.setMaxDate(this.props.datepickerOptions?.max);
    observe(this.field, ['props', 'datepickerOptions', 'min'], () => {
      this.setMinDate(this.props.datepickerOptions?.min);
    });
    observe(this.field, ['props', 'datepickerOptions', 'max'], () => {
      this.setMaxDate(this.props.datepickerOptions?.max);
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    merge(
      this.datepicker.closedStream,
      fromEvent(this.input.nativeElement, 'blur')
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.formControl.setValue(
          this.dateFnsHelpers.toNeutralDate(this.formControl.value)
        );
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setMinDate(value: string | Date): void {
    this.min = this.buildDate(value);
  }

  private setMaxDate(value: string | Date): void {
    this.max = this.buildDate(value);
  }

  private buildDate(date: string | Date): Date {
    if (date instanceof Date || isNil(date)) {
      return date;
    }

    const [startDate, action, duration]: string[] = split(
      replace(date, 'calc=', ''),
      '|'
    );

    switch (action) {
      case 'add':
        return add(this.getStartDate(startDate), JSON.parse(duration));

      case 'sub':
        return sub(this.getStartDate(startDate), JSON.parse(duration));

      default:
        return this.getStartDate(startDate);
    }
  }

  private getStartDate(value: string): Date {
    return value === 'now'
      ? new Date()
      : toDate(parse(value, 'yyyy/MM/dd', new Date()));
  }
}
